/**
 * Content Splitter Utility
 * 
 * Splits the combined training markdown content into separate Training 1 and Training 2 sections.
 * This utility is used by the dashboard and individual training pages.
 */

export interface SplitTrainingContent {
  training1: string;
  training2: string;
  full: string;
}

/**
 * Splits the full training markdown content into Training 1 and Training 2 sections.
 * 
 * Supports both H1 (# Training 1) and H2 (## Training 1) markers for flexibility.
 * 
 * @param fullContent - The complete markdown content from the file
 * @returns Object containing training1, training2, and full content strings
 */
export function splitTrainingContent(fullContent: string): SplitTrainingContent {
  // Find the markers for Training 1 and Training 2
  // Support both H1 (# Training 1) and H2 (## Training 1) formats
  const training1Marker = /^#+ Training 1[^\n]*/m;
  const training2Marker = /^#+ Training 2[^\n]*/m;

  const training1Match = fullContent.match(training1Marker);
  const training2Match = fullContent.match(training2Marker);

  // If markers are not found, return the full content for both
  if (!training1Match || !training2Match) {
    return {
      training1: fullContent,
      training2: fullContent,
      full: fullContent
    };
  }

  const training1StartIndex = training1Match.index!;
  const training2StartIndex = training2Match.index!;

  // Extract the header (everything before Training 1)
  const header = fullContent.substring(0, training1StartIndex).trim();

  // Extract Training 1 content (from Training 1 marker to Training 2 marker)
  const training1Content = fullContent
    .substring(training1StartIndex, training2StartIndex)
    .trim();

  // Extract Training 2 content (from Training 2 marker to end)
  const training2Content = fullContent.substring(training2StartIndex).trim();

  // Combine header with each training section
  const training1 = header ? `${header}\n\n${training1Content}` : training1Content;
  const training2 = header ? `${header}\n\n${training2Content}` : training2Content;

  return {
    training1,
    training2,
    full: fullContent
  };
}

/**
 * Gets a specific training's content by number (1 or 2)
 * 
 * @param fullContent - The complete markdown content from the file
 * @param trainingNumber - The training number (1 or 2)
 * @returns The content for the specified training
 */
export function getTrainingContent(fullContent: string, trainingNumber: 1 | 2): string {
  const split = splitTrainingContent(fullContent);
  return trainingNumber === 1 ? split.training1 : split.training2;
}
